import type { Customer } from 'src/types/types';

import { toast } from 'sonner';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Card,
  Switch,
  Button,
  Divider,
  Collapse,
  CardHeader,
  FormControlLabel,
} from '@mui/material';

import { createCustomer } from 'src/data/api'; // <-- add updateCustomer import
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { setAllCustomers } from 'src/features/customerSlice/customerSlice';

import { Field } from 'src/components/hook-form';

interface Props {
  onClose: () => void;
  onCreateCallback: (customer: Customer) => void;
  editingCustomer?: Customer | null;
}

export function CreateEditCustomer(props: Props) {
  const { onClose, onCreateCallback, editingCustomer } = props;

  // State declarations
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<{ tekst: string } | null>(null);
  const [addressInput, setAddressInput] = useState('');
  const [addressOptions, setAddressOptions] = useState<{ tekst: string }[]>([]);
  const [company, setCompany] = useState('');
  const [companyCvr, setCompanyCvr] = useState('');
  const [primary, setPrimary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompanyFields, setShowCompanyFields] = useState(false);

  const dispatch = useAppDispatch();
  const customers = useAppSelector((state) => state.customers.customers);

  // Initialize fields if editingCustomer is present
  useEffect(() => {
    if (editingCustomer) {
      setName(editingCustomer.name || '');
      setPhone(editingCustomer.phone || '');
      if (typeof editingCustomer.address === 'string') {
        setAddress(null);
        setAddressInput(editingCustomer.address);
      } else if (editingCustomer.address && typeof editingCustomer.address === 'object') {
        setAddress(editingCustomer.address);
        setAddressInput(editingCustomer.address);
      } else {
        setAddress(null);
        setAddressInput('');
      }

      if (editingCustomer.company) {
        setShowCompanyFields(true);
        setCompany(editingCustomer.company);
        setCompanyCvr(editingCustomer.companyCvr || '');
      } else {
        setShowCompanyFields(false);
        setCompany('');
        setCompanyCvr('');
      }
      setPrimary(!!editingCustomer.primary);
    } else {
      // Reset form if no editing customer
      setName('');
      setPhone('');
      setAddress(null);
      setAddressInput('');
      setCompany('');
      setCompanyCvr('');
      setPrimary(false);
      setShowCompanyFields(false);
    }
  }, [editingCustomer]);

  useEffect(() => {
    if (!addressInput) {
      setAddressOptions([]);
      return;
    }

    fetch(`https://dawa.aws.dk/adresser/autocomplete?q=${encodeURIComponent(addressInput)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Netværksfejl');
        return res.json();
      })
      .then((data) => {
        setAddressOptions(data);
      })
      .catch(() => setAddressOptions([]));
  }, [addressInput]);

  useEffect(() => {
    if (!showCompanyFields) {
      setCompany('');
      setCompanyCvr('');
      return;
    }

    if (companyCvr.length !== 8) return;

    fetch(`https://cvrapi.dk/api?search=${companyCvr}&country=dk`, {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Netværksfejl');
        return response.json();
      })
      .then((data) => {
        setCompany(data.name || '');
      })
      .catch((error) => {
        console.error('Fejl ved opslag:', error);
        setCompany('');
      });
  }, [companyCvr, showCompanyFields]);

  const validate = () => {
    if (!name.trim()) {
      toast.error('Navn er påkrævet');
      return false;
    }

    const phoneRegex = /^\d{8}$/;
    if (phone && !phoneRegex.test(phone)) {
      toast.error('Telefonnummer skal være 8 cifre');
      return false;
    }

    const cvrRegex = /^\d{8}$/;
    if (showCompanyFields && companyCvr && !cvrRegex.test(companyCvr)) {
      toast.error('CVR nummer skal være 8 cifre');
      return false;
    }

    return true;
  };

  const saveCustomerHandler = () => {
    if (!validate()) return;

    setLoading(true);

    const body = {
      name,
      phone,
      address: address ? address.tekst : addressInput,
      company: showCompanyFields ? company : '',
      companyCvr: showCompanyFields ? companyCvr : '',
      primary,
    };

    if (editingCustomer) {
      // Update existing customer
      createCustomer({ id: editingCustomer.id, ...body })
        .then((updatedCustomer) => {
          toast.success('Kunde opdateret!');
          // Update customers in redux
          const updatedCustomers = customers.map((cust) =>
            cust.id === updatedCustomer.id ? updatedCustomer : cust
          );
          dispatch(setAllCustomers(updatedCustomers));
          onCreateCallback(updatedCustomer);
          onClose();
        })
        .catch((e) => {
          toast.error(e.message || 'Noget gik galt ved opdatering');
        })
        .finally(() => setLoading(false));
    } else {
      // Create new customer
      createCustomer(body)
        .then((newCustomer) => {
          toast.success('Ny kunde oprettet!');
          dispatch(setAllCustomers([...customers, newCustomer]));
          onCreateCallback(newCustomer);
          onClose();
        })
        .catch((e) => {
          toast.error(e.message || 'Noget gik galt');
        })
        .finally(() => setLoading(false));
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <Card>
        <CardHeader
          title={editingCustomer ? 'Rediger kunde' : 'Ny kunde'}
          subheader="Navn, telefonnummer, firma..."
          sx={{ mb: 3 }}
          action={
            <FormControlLabel
              control={
                <Switch
                  id="toggle-show-company"
                  checked={showCompanyFields}
                  onChange={(e) => setShowCompanyFields(e.target.checked)}
                  color="primary"
                />
              }
              label="Erhverv"
              labelPlacement="start"
              sx={{
                userSelect: 'none',
                mr: 0,
                '& .MuiFormControlLabel-label': {
                  fontWeight: 'bold',
                  fontSize: '1rem',
                },
              }}
            />
          }
        />

        <Collapse in>
          <Divider />

          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {/* Navn */}
              <Box sx={{ flexBasis: { xs: '100%', sm: '48%' } }}>
                <Field.Text
                  name="name"
                  label="Navn"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
              </Box>

              {/* Telefon */}
              <Box sx={{ flexBasis: { xs: '100%', sm: '48%' } }}>
                <Field.Text
                  name="phone"
                  label="Telefon"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                />
              </Box>

              {/* Adresse */}
              <Box sx={{ flexBasis: '100%' }}>
                <Autocomplete
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.tekst)}
                  freeSolo
                  options={addressOptions}
                  inputValue={addressInput}
                  onInputChange={(_event, newInputValue) => setAddressInput(newInputValue)}
                  value={address}
                  onChange={(_event, newValue: any) => setAddress(newValue)}
                  renderInput={(params) => <TextField {...params} label="Adresse" fullWidth />}
                />
              </Box>

              {/* Firma felter */}
              {showCompanyFields && (
                <>
                  <Box sx={{ flexBasis: { xs: '100%', sm: '48%' } }}>
                    <Field.Text
                      name="cvr"
                      label="CVR nummer"
                      value={companyCvr}
                      onChange={(e) => setCompanyCvr(e.target.value)}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flexBasis: { xs: '100%', sm: '48%' } }}>
                    <Field.Text disabled name="company" label="Firma" value={company} fullWidth />
                  </Box>
                </>
              )}

              {/* Nederste række med primær toggle og knap */}
              <Box
                sx={{
                  flexBasis: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 3,
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                {/* Primær toggle – mindre og diskret */}
                <FormControlLabel
                  control={
                    <Switch
                      id="toggle-primary"
                      checked={primary}
                      onChange={(e) => setPrimary(e.target.checked)}
                      color="default"
                      size="small"
                    />
                  }
                  label="Primær"
                  sx={{
                    userSelect: 'none',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    ml: 1,
                  }}
                />

                <Button
                  onClick={saveCustomerHandler}
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: 150 }}
                >
                  {loading
                    ? editingCustomer
                      ? 'Opdaterer...'
                      : 'Opretter...'
                    : editingCustomer
                      ? 'Opdater kunde'
                      : 'Opret kunde'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Card>
    </Dialog>
  );
}
